import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Users,
  BarChart3,
  ArrowRight,
  CheckCircle,
  Shield,
  Trophy,
  Swords,
  Dumbbell,
  ShoppingCart,
  Zap,
  Target,
  Crown,
  Copy,
  Share2,
  Link2,
  Medal,
} from 'lucide-react';
import Footer from '../components/Footer';
import { Header } from '../components/Header';
import Seo from '../components/Seo';

const ease = [0.16, 1, 0.3, 1];

const FeatureCard = ({ icon, title, description, delay, href }) => {
  const inner = (
    <motion.div
      className="bg-[var(--surface-1)] rounded-xl p-6 hover:translate-y-[-4px] transition-all duration-300 h-full"
      style={{ border: '1px solid var(--border-edge)' }}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
    >
      <div className="mb-4">{icon}</div>
      <h3 className="text-lg font-bold mb-2">{title}</h3>
      <p className="text-sm text-[var(--text-3)] leading-relaxed">{description}</p>
      {href && <span className="inline-block mt-3 text-xs font-bold text-[var(--accent)]">Learn more →</span>}
    </motion.div>
  );
  return href ? <Link to={href}>{inner}</Link> : inner;
};

const StepCard = ({ number, title, description, delay }) => (
  <motion.div
    className="flex gap-4"
    initial={{ opacity: 0, x: -20 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay }}
  >
    <div
      className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg flex-shrink-0"
      style={{ background: 'var(--accent)', color: 'var(--surface-0)' }}
    >
      {number}
    </div>
    <div>
      <h4 className="font-bold mb-1">{title}</h4>
      <p className="text-sm text-[var(--text-3)] leading-relaxed">{description}</p>
    </div>
  </motion.div>
);

const StatPill = ({ icon, label, value }) => (
  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs" style={{ background: 'var(--surface-2)' }}>
    {icon}
    <span className="text-[var(--text-3)]">{label}</span>
    <span className="font-bold" style={{ color: 'var(--accent)' }}>{value}</span>
  </div>
);

const homeJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is My Football Game?',
      acceptedAnswer: { '@type': 'Answer', text: 'My Football Game is a free online football manager game you play in your browser. Create your club, build a squad, set tactics, sign players on the transfer market and compete in live simulated matches against managers worldwide. No download needed.' }
    },
    {
      '@type': 'Question',
      name: 'Is My Football Game free to play?',
      acceptedAnswer: { '@type': 'Answer', text: 'Yes, My Football Game is completely free. You can optionally buy gold coins for booster packs, but everything is accessible without spending money.' }
    },
    {
      '@type': 'Question',
      name: 'How do I play My Football Game?',
      acceptedAnswer: { '@type': 'Answer', text: 'Register at myfootballgame.com, get your starting squad, set your formation and tactics, then challenge other managers to live simulated matches. Win to earn coins, climb leagues and get promoted.' }
    },
    {
      '@type': 'Question',
      name: 'Can I play football manager games online for free?',
      acceptedAnswer: { '@type': 'Answer', text: 'Yes. My Football Game is a free browser-based football manager with no download required. Play on desktop, tablet or mobile at myfootballgame.com.' }
    },
  ]
};

const LandingPage = () => {
  return (
    <div className="min-h-[100dvh]" style={{ background: 'var(--surface-0)', color: 'var(--text-1)' }}>
      <Seo
        title="My Football Game – Free Online Football Manager, No Download"
        description="Free football manager in your browser, no download. Build your club from scratch, set tactics, sign players and beat friends in PvP leagues. Start in 30s."
        path="/"
        alternates={[['en', '/'], ['pt', '/pt'], ['es', '/es'], ['x-default', '/']]}
        jsonLd={homeJsonLd}
      />

      {/* ── HERO ── */}
      <div className="container mx-auto px-4 pt-8 pb-24 relative">
        <Header />

        <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-20" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          <g stroke="rgba(184,255,61,0.06)" strokeWidth="0.5">
            {[...Array(12)].map((_, i) => (
              <line key={`v${i}`} x1={120 + i * 100} y1="180" x2={120 + i * 100} y2="720" />
            ))}
            {[...Array(6)].map((_, i) => (
              <line key={`h${i}`} x1="120" y1={180 + i * 108} x2="1320" y2={180 + i * 108} />
            ))}
          </g>
        </svg>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10">
          {/* Above the fold renders visible immediately: fade-ins here kept phones on a blank screen. */}
          <motion.div initial={false} animate={{ opacity: 1, x: 0 }}>
            <div className="pc-eyebrow pc-eyebrow-accent mb-3">My Football Game · Free to Play</div>
            <motion.h1
              className="pc-display text-5xl md:text-7xl lg:text-8xl mb-6 leading-[0.85]"
              initial={false}
              animate={{ opacity: 1, y: 0 }}
            >
              FREE ONLINE<br />
              <span style={{ color: 'var(--accent)' }}>FOOTBALL MANAGER</span> GAME
            </motion.h1>
            <motion.p
              className="text-base md:text-lg mb-8 max-w-xl"
              style={{ color: 'var(--text-2)' }}
              initial={false}
              animate={{ opacity: 1, y: 0 }}
            >
              Create your club, sign players from the transfer market, set your tactics,
              and challenge other managers in real-time simulated matches. Climb the leagues and prove you're the best.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row gap-3"
              initial={false}
              animate={{ opacity: 1, y: 0 }}
            >
              <Link to="/register">
                <motion.button
                  className="px-7 py-3 rounded font-bold text-[13px] uppercase tracking-[0.2em] flex items-center justify-center gap-2 w-full sm:w-auto transition-all"
                  style={{
                    background: 'var(--accent)',
                    color: 'var(--surface-0)',
                    fontFamily: 'var(--font-body)',
                    boxShadow: '0 0 24px var(--accent-glow)',
                  }}
                  whileHover={{ y: -2 }}
                  whileTap={{ y: 0 }}
                >
                  Play Free <ArrowRight className="h-4 w-4" />
                </motion.button>
              </Link>
              <Link to="/login">
                <motion.button
                  className="px-7 py-3 rounded font-bold text-[13px] uppercase tracking-[0.2em] flex items-center justify-center gap-2 w-full sm:w-auto transition-all"
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border-edge)',
                    color: 'var(--text-1)',
                    fontFamily: 'var(--font-body)',
                  }}
                  whileHover={{ y: -2 }}
                  whileTap={{ y: 0 }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-edge)'; e.currentTarget.style.color = 'var(--text-1)'; }}
                >
                  Login
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>

          {/* Hero visual — animated match scoreboard */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative hidden lg:block"
          >
            <div className="relative rounded-2xl overflow-hidden" style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)' }}>
              {/* Match header */}
              <div className="px-6 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-edge)' }}>
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--accent)' }}>Live Match</span>
                <motion.span
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}
                  animate={{ opacity: [1, 0.4, 1] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                >
                  72'
                </motion.span>
              </div>

              {/* Scoreboard */}
              <div className="px-8 py-8">
                <div className="flex items-center justify-between mb-8">
                  <div className="text-center flex-1">
                    <div className="w-14 h-14 rounded-full mx-auto mb-2 flex items-center justify-center text-xl font-bold" style={{ background: 'var(--accent)', color: 'var(--surface-0)' }}>FF</div>
                    <p className="font-bold text-sm">Your Club</p>
                  </div>
                  <div className="flex items-center gap-4 px-6">
                    <motion.span
                      className="pc-display text-5xl"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 1.2, type: 'spring' }}
                    >3</motion.span>
                    <span className="text-[var(--text-3)] text-2xl">-</span>
                    <motion.span
                      className="pc-display text-5xl"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 1.4, type: 'spring' }}
                    >1</motion.span>
                  </div>
                  <div className="text-center flex-1">
                    <div className="w-14 h-14 rounded-full mx-auto mb-2 flex items-center justify-center text-xl font-bold" style={{ background: 'var(--surface-2)' }}>RL</div>
                    <p className="font-bold text-sm">Rival FC</p>
                  </div>
                </div>

                {/* Match events */}
                <div className="space-y-2">
                  {[
                    { min: "12'", event: 'Goal — Pedro Silva', side: 'left' },
                    { min: "34'", event: 'Goal — Lucas Costa', side: 'left' },
                    { min: "51'", event: 'Goal — Opponent', side: 'right' },
                    { min: "67'", event: 'Goal — Rafael Santos', side: 'left' },
                  ].map((e, i) => (
                    <motion.div
                      key={i}
                      className={`flex items-center gap-3 text-xs py-1.5 px-3 rounded ${e.side === 'left' ? '' : 'flex-row-reverse text-right'}`}
                      style={{ background: e.side === 'left' ? 'rgba(184,255,61,0.06)' : 'transparent' }}
                      initial={{ opacity: 0, x: e.side === 'left' ? -20 : 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.6 + i * 0.2 }}
                    >
                      <span className="font-bold" style={{ color: 'var(--accent)' }}>{e.min}</span>
                      <span className="text-[var(--text-2)]">{e.event}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Bottom stats */}
              <div className="px-6 py-3 flex justify-center gap-3 flex-wrap" style={{ borderTop: '1px solid var(--border-edge)' }}>
                <StatPill icon={<Target className="w-3 h-3" />} label="Shots" value="12 - 4" />
                <StatPill icon={<Shield className="w-3 h-3" />} label="Possession" value="63%" />
                <StatPill icon={<Zap className="w-3 h-3" />} label="Rating" value="8.2" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Stats strip */}
        <motion.div
          className="mt-20 py-6 px-4 sm:px-8 relative z-10 rounded-xl"
          style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)' }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {[
              { v: '18', l: 'Formations' },
              { v: '26', l: 'Player Positions' },
              { v: '5', l: 'League Tiers' },
              { v: 'Free', l: 'To Play' },
            ].map((stat, i) => (
              <motion.div
                key={stat.l}
                className="text-center"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 + i * 0.08, ease }}
              >
                <div className="pc-display text-4xl sm:text-5xl" style={{ color: 'var(--accent)' }}>{stat.v}</div>
                <div className="pc-eyebrow mt-1">{stat.l}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* ── FEATURES ── */}
      <section id="features" className="py-20" style={{ background: 'var(--surface-1)' }}>
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Everything you need to compete</h2>
            <p className="text-lg text-[var(--text-2)] max-w-2xl mx-auto">
              A complete football management experience — from squad building to live match simulation.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard
              icon={<Users className="w-8 h-8 text-[var(--accent)]" />}
              title="Squad management"
              description="Build your 11 + substitutes. Choose from 18 formations and assign detailed positions like CAM, RB, or CDM."
              delay={0.1}
              href="/features/tactics"
            />
            <FeatureCard
              icon={<Swords className="w-8 h-8 text-[var(--accent)]" />}
              title="Challenge friends"
              description="Send challenges to friends or random opponents in your league. 3 daily challenges with bonus rewards."
              delay={0.2}
              href="/features/live-matches"
            />
            <FeatureCard
              icon={<Shield className="w-8 h-8 text-[var(--accent)]" />}
              title="Live match simulation"
              description="Watch matches unfold in real-time with goals, cards, injuries, and substitutions streamed live."
              delay={0.3}
              href="/features/live-matches"
            />
            <FeatureCard
              icon={<Dumbbell className="w-8 h-8 text-[var(--accent)]" />}
              title="Player training"
              description="Train individual stats. Young players grow faster, veterans bring experience. Age and physique matter."
              delay={0.4}
              href="/features/training"
            />
            <FeatureCard
              icon={<ShoppingCart className="w-8 h-8 text-[var(--accent)]" />}
              title="Transfer market"
              description="Buy and sell players. Scout the market for bargains or sell your stars for millions."
              delay={0.5}
              href="/features/transfer-market"
            />
            <FeatureCard
              icon={<Trophy className="w-8 h-8 text-[var(--accent)]" />}
              title="Private leagues"
              description="Create invite-only leagues with friends. Compete for gold prizes over full seasons."
              delay={0.6}
              href="/features/leagues"
            />
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="gameplay" className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-3">How it works</h2>
              <p className="text-[var(--text-2)] mb-8">From sign-up to your first match in under 2 minutes.</p>

              <div className="space-y-6">
                <StepCard number="1" title="Create your club" description="Pick a name, get a squad of generated players with unique stats, ages, and nationalities." delay={0.1} />
                <StepCard number="2" title="Set your formation and lineup" description="Choose from 18 formations. Assign players to their best positions — familiarity boosts their performance." delay={0.2} />
                <StepCard number="3" title="Challenge opponents" description="Send challenges to other managers in your league. Matches simulate automatically with full event logs." delay={0.3} />
                <StepCard number="4" title="Climb the ranks" description="Win matches to earn coins, league points, and gold. Get promoted to higher tiers with tougher competition." delay={0.4} />
              </div>
            </motion.div>

            {/* Game loop visual */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="rounded-2xl p-8" style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)' }}>
                <h3 className="text-xs font-bold uppercase tracking-widest mb-6" style={{ color: 'var(--accent)' }}>The game loop</h3>
                <div className="space-y-4">
                  {[
                    { icon: <Users className="w-5 h-5" />, label: 'Manage squad', sub: '26 positions, physique system' },
                    { icon: <Target className="w-5 h-5" />, label: 'Set tactics', sub: '18 formations, set pieces' },
                    { icon: <Swords className="w-5 h-5" />, label: 'Play matches', sub: 'Real-time SSE streaming' },
                    { icon: <BarChart3 className="w-5 h-5" />, label: 'Earn rewards', sub: 'Coins, gold, league points' },
                    { icon: <ShoppingCart className="w-5 h-5" />, label: 'Improve team', sub: 'Training, transfers, booster packs' },
                    { icon: <Crown className="w-5 h-5" />, label: 'Get promoted', sub: '5 league tiers to conquer' },
                  ].map((item, i) => (
                    <motion.div
                      key={i}
                      className="flex items-center gap-4 p-3 rounded-lg transition-colors hover:bg-[var(--surface-2)]"
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 + i * 0.1 }}
                    >
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(184,255,61,0.1)', color: 'var(--accent)' }}>
                        {item.icon}
                      </div>
                      <div>
                        <p className="font-bold text-sm">{item.label}</p>
                        <p className="text-xs text-[var(--text-3)]">{item.sub}</p>
                      </div>
                      {i < 5 && (
                        <div className="ml-auto text-[var(--text-3)]">
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── LEAGUES ── */}
      <section id="leagues" className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Climb the league ladder</h2>
            <p className="text-lg text-[var(--text-2)] max-w-2xl mx-auto">
              Five divisions, 18 matchdays a season. The top two go up, the bottom two go down, and every step up means tougher opponents and a bigger prize.
            </p>
          </motion.div>

          {/* League tiers */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 max-w-5xl mx-auto mb-16">
            {[
              { tier: 1, name: 'Amateur League', color: '#6b7280', reward: '60K' },
              { tier: 2, name: 'Regional League', color: '#3b82f6', reward: '120K' },
              { tier: 3, name: 'National League', color: '#8b5cf6', reward: '200K' },
              { tier: 4, name: 'Championship', color: '#f59e0b', reward: '320K' },
              { tier: 5, name: 'Elite League', color: '#ef4444', reward: '500K' },
            ].map((league, i) => (
              <motion.div
                key={league.tier}
                className="rounded-xl p-5 text-center relative overflow-hidden"
                style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)' }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <div className="absolute inset-0 opacity-5" style={{ background: `radial-gradient(circle at 50% 0%, ${league.color}, transparent 70%)` }} />
                <div className="relative z-10">
                  <Trophy className="w-8 h-8 mx-auto mb-2" style={{ color: league.color }} />
                  <p className="font-bold text-sm mb-1">{league.name}</p>
                  <p className="text-xs text-[var(--text-3)]">Tier {league.tier}</p>
                  <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border-edge)' }}>
                    <p className="text-xs text-[var(--text-3)]">Base season prize</p>
                    <p className="font-bold text-sm" style={{ color: 'var(--accent)' }}>{league.reward} coins</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Private Leagues — visual mockups */}
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl md:text-3xl font-bold mb-3">Play with friends</h3>
            <p className="text-[var(--text-2)] max-w-xl mx-auto">
              Create a private league, share the invite code, and compete head-to-head for bragging rights and gold prizes.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Card 1: Share invite */}
            <motion.div
              className="rounded-2xl overflow-hidden"
              style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid var(--border-edge)' }}>
                <Link2 className="w-4 h-4" style={{ color: 'var(--accent)' }} />
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--accent)' }}>Invite friends</span>
              </div>
              <div className="p-5">
                <p className="text-sm text-[var(--text-2)] mb-4">Share your league code and get your group playing in seconds.</p>
                <div className="rounded-lg p-3 flex items-center justify-between mb-3" style={{ background: 'var(--surface-2)', border: '1px solid var(--border-edge)' }}>
                  <span className="font-mono font-bold text-sm tracking-wider" style={{ color: 'var(--accent)' }}>XKCD-7F2A</span>
                  <div className="flex items-center gap-1 text-xs text-[var(--text-3)] cursor-default">
                    <Copy className="w-3.5 h-3.5" />
                    Copy
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 rounded-lg py-2 text-center text-xs font-bold flex items-center justify-center gap-1.5" style={{ background: 'rgba(37,211,102,0.1)', color: '#25D366', border: '1px solid rgba(37,211,102,0.2)' }}>
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" /><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.612.616l4.529-1.476A11.96 11.96 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.239 0-4.308-.724-5.993-1.953l-.419-.306-2.694.879.897-2.651-.335-.434A9.96 9.96 0 012 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z" /></svg>
                    WhatsApp
                  </div>
                  <div className="flex-1 rounded-lg py-2 text-center text-xs font-bold flex items-center justify-center gap-1.5" style={{ background: 'rgba(184,255,61,0.1)', color: 'var(--accent)', border: '1px solid rgba(184,255,61,0.2)' }}>
                    <Share2 className="w-3.5 h-3.5" /> Share
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Card 2: League standings */}
            <motion.div
              className="rounded-2xl overflow-hidden"
              style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid var(--border-edge)' }}>
                <Trophy className="w-4 h-4" style={{ color: 'var(--accent)' }} />
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--accent)' }}>Standings</span>
              </div>
              <div className="p-5">
                <div className="space-y-2">
                  {[
                    { pos: 1, name: 'Your Club', pts: 24, w: 8, color: 'var(--accent)' },
                    { pos: 2, name: 'Mike FC', pts: 21, w: 7, color: 'var(--text-1)' },
                    { pos: 3, name: 'João United', pts: 18, w: 6, color: 'var(--text-1)' },
                    { pos: 4, name: 'Alex City', pts: 12, w: 4, color: 'var(--text-3)' },
                    { pos: 5, name: 'Sara Athletic', pts: 9, w: 3, color: 'var(--text-3)' },
                  ].map((team, i) => (
                    <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${i === 0 ? '' : ''}`} style={i === 0 ? { background: 'rgba(184,255,61,0.06)' } : {}}>
                      <span className="w-5 text-center font-bold text-xs" style={{ color: i === 0 ? 'var(--accent)' : 'var(--text-3)' }}>{team.pos}</span>
                      {i === 0 && <Medal className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#f59e0b' }} />}
                      <span className="flex-1 font-medium text-sm" style={{ color: team.color }}>{team.name}</span>
                      <span className="text-xs text-[var(--text-3)]">{team.w}W</span>
                      <span className="font-bold text-xs w-8 text-right" style={{ color: i === 0 ? 'var(--accent)' : 'var(--text-2)' }}>{team.pts}p</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Card 3: Share match result */}
            <motion.div
              className="rounded-2xl overflow-hidden"
              style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid var(--border-edge)' }}>
                <Share2 className="w-4 h-4" style={{ color: 'var(--accent)' }} />
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--accent)' }}>Share result</span>
              </div>
              <div className="p-5">
                <div className="rounded-xl p-4 mb-4" style={{ background: 'var(--surface-2)', border: '1px solid var(--border-edge)' }}>
                  <p className="text-[10px] uppercase tracking-widest text-[var(--text-3)] mb-3">Private League · Final</p>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-center flex-1">
                      <div className="w-10 h-10 rounded-full mx-auto mb-1 flex items-center justify-center text-sm font-bold" style={{ background: 'var(--accent)', color: 'var(--surface-0)' }}>FF</div>
                      <p className="font-bold text-xs">Your Club</p>
                    </div>
                    <div className="flex items-center gap-2 px-3">
                      <span className="pc-display text-3xl">4</span>
                      <span className="text-[var(--text-3)]">-</span>
                      <span className="pc-display text-3xl">2</span>
                    </div>
                    <div className="text-center flex-1">
                      <div className="w-10 h-10 rounded-full mx-auto mb-1 flex items-center justify-center text-sm font-bold" style={{ background: 'var(--surface-1)' }}>MF</div>
                      <p className="font-bold text-xs">Mike FC</p>
                    </div>
                  </div>
                  <div className="flex justify-center gap-4 text-[10px] text-[var(--text-3)]">
                    <span>Shots: 14 - 8</span>
                    <span>Poss: 58%</span>
                    <span>MOTM: Pedro S.</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 rounded-lg py-2 text-center text-xs font-bold flex items-center justify-center gap-1.5" style={{ background: 'rgba(184,255,61,0.1)', color: 'var(--accent)', border: '1px solid rgba(184,255,61,0.2)' }}>
                    <Share2 className="w-3.5 h-3.5" /> Share
                  </div>
                  <div className="flex-1 rounded-lg py-2 text-center text-xs font-bold flex items-center justify-center gap-1.5" style={{ background: 'var(--surface-2)', color: 'var(--text-2)', border: '1px solid var(--border-edge)' }}>
                    <BarChart3 className="w-3.5 h-3.5" /> Full stats
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Promotion & relegation bullets */}
          <motion.div
            className="mt-12 max-w-3xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {[
              { icon: <Crown className="w-5 h-5" />, text: 'Promotion & relegation' },
              { icon: <BarChart3 className="w-5 h-5" />, text: 'Global leaderboards' },
              { icon: <Medal className="w-5 h-5" />, text: 'Gold coin prizes' },
              { icon: <Users className="w-5 h-5" />, text: 'Unlimited members' },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center text-center gap-2 p-3 rounded-xl" style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)' }}>
                <div style={{ color: 'var(--accent)' }}>{item.icon}</div>
                <p className="text-xs font-bold text-[var(--text-2)]">{item.text}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── VIDEO ── */}
      <section id="video" className="py-20" style={{ background: 'var(--surface-1)' }}>
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">See it in action</h2>
            <p className="text-lg text-[var(--text-2)] max-w-2xl mx-auto">
              Watch a full match simulation and see the management experience.
            </p>
          </motion.div>

          <motion.div
            className="relative max-w-4xl mx-auto rounded-xl overflow-hidden"
            style={{ border: '1px solid var(--border-edge)' }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="relative pb-[56.25%] h-0">
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/TjkYA0S1IJ4?si=6KFaudxGTrc_X8xf"
                title="My Football Game Gameplay"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── ECONOMY ── */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Two currencies, no paywall</h2>
            <p className="text-lg text-[var(--text-2)] max-w-2xl mx-auto">
              Earn coins from matches. Win gold from victories. Everything is earnable through gameplay.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { title: 'Coins', desc: 'Earned every match. Use for training, transfers, and daily challenges.', color: '#f59e0b' },
              { title: 'Gold', desc: 'Win matches to earn gold. Use for booster packs, private leagues, and premium items.', color: 'var(--accent)' },
              { title: 'Store', desc: 'Player packs, stat boosts, extra challenge slots, and cosmetic items.', color: '#8b5cf6' },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="rounded-xl p-6 text-center"
                style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)' }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: `${item.color}20`, color: item.color }}>
                  {i === 0 ? <span className="text-xl font-bold">$</span> : i === 1 ? <Crown className="w-5 h-5" /> : <ShoppingCart className="w-5 h-5" />}
                </div>
                <h3 className="font-bold mb-2">{item.title}</h3>
                <p className="text-sm text-[var(--text-3)] leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMMUNITY ── */}
      <section className="py-20" style={{ background: 'var(--surface-1)' }}>
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Join the community</h2>
            <p className="text-lg text-[var(--text-2)] max-w-2xl mx-auto">
              Follow us for tips, updates, patch notes and community highlights. Connect with other managers worldwide.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 max-w-4xl mx-auto">
            {[
              { name: 'X / Twitter', href: 'https://x.com/myfootballgame', color: '#000000', icon: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z', sub: 'Updates & news' },
              { name: 'Instagram', href: 'https://www.instagram.com/myfootballgame', color: '#E4405F', icon: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z', sub: 'Screenshots & clips' },
              { name: 'TikTok', href: 'https://www.tiktok.com/@myfootballgame', color: '#00f2ea', icon: 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z', sub: 'Gameplay videos' },
              { name: 'YouTube', href: 'https://www.youtube.com/@myfootballgame', color: '#FF0000', icon: 'M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z', sub: 'Tutorials & guides' },
              { name: 'Reddit', href: 'https://www.reddit.com/r/myfootballgame', color: '#FF4500', icon: 'M12 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 01-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 01.042.52c0 2.694-3.13 4.884-7.003 4.884-3.874 0-7.004-2.19-7.004-4.884 0-.18.015-.36.043-.534A1.748 1.748 0 014.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 01.14-.197.35.35 0 01.238-.042l2.906.617a1.214 1.214 0 011.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 00-.231.094.33.33 0 000 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 00.029-.463.33.33 0 00-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 00-.232-.095z', sub: 'Community & strategy' },
              { name: 'Discord', href: 'https://discord.gg/yHmVeqZvyC', color: '#5865F2', icon: 'M20.317 4.369a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.078.037c-.21.375-.444.864-.608 1.249a18.219 18.219 0 00-5.487 0 12.509 12.509 0 00-.617-1.25.077.077 0 00-.078-.037 19.736 19.736 0 00-4.885 1.515.07.07 0 00-.032.027C2.532 9.045 1.917 13.579 2.293 18.057a.082.082 0 00.031.056 19.9 19.9 0 005.993 3.036.077.077 0 00.084-.027c.461-.63.873-1.295 1.226-1.994a.076.076 0 00-.041-.106 12.571 12.571 0 01-1.792-.86.078.078 0 01-.008-.13c.12-.09.24-.183.355-.278a.074.074 0 01.077-.01c3.747 1.714 7.791 1.714 11.508 0a.075.075 0 01.078.009c.115.095.235.188.356.278a.078.078 0 01-.006.13 12.32 12.32 0 01-1.793.86.076.076 0 00-.04.106c.36.698.772 1.363 1.225 1.994a.076.076 0 00.084.028 19.876 19.876 0 005.994-3.037.077.077 0 00.03-.055c.5-5.177-.838-9.672-3.548-13.661a.061.061 0 00-.03-.028zM8.02 15.331c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.955-2.418 2.157-2.418 1.21 0 2.175 1.093 2.157 2.419 0 1.334-.955 2.418-2.157 2.418zm7.974 0c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.955-2.418 2.157-2.418 1.21 0 2.175 1.093 2.157 2.419 0 1.334-.947 2.418-2.157 2.418z', sub: 'Chat & matchmaking' },
            ].map((social, i) => (
              <motion.a
                key={social.name}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 p-4 rounded-xl transition-all hover:translate-y-[-4px]"
                style={{ background: 'var(--surface-2)', border: '1px solid var(--border-edge)' }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                whileHover={{ borderColor: social.color }}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${social.color}15` }}>
                  <svg className="w-5 h-5" fill={social.color} viewBox="0 0 24 24" aria-hidden="true">
                    <path d={social.icon} />
                  </svg>
                </div>
                <p className="font-bold text-xs">{social.name}</p>
                <p className="text-[10px] text-[var(--text-3)]">{social.sub}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-3xl mx-auto rounded-2xl p-8 md:p-16 text-center relative overflow-hidden"
            style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)' }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="absolute inset-0 opacity-5" style={{ background: 'radial-gradient(circle at 50% 0%, var(--accent), transparent 60%)' }} />

            <motion.h2
              className="text-3xl md:text-4xl font-bold mb-4 relative z-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              Ready to manage?
            </motion.h2>
            <motion.p
              className="text-lg text-[var(--text-2)] mb-8 relative z-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              Create your club, set your lineup, and send your first challenge — all free.
            </motion.p>
            <motion.div
              className="relative z-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
            >
              <Link to="/register">
                <motion.button
                  className="px-8 py-4 rounded-lg font-bold text-[13px] uppercase tracking-[0.2em] inline-flex items-center gap-2 transition-all"
                  style={{
                    background: 'var(--accent)',
                    color: 'var(--surface-0)',
                    boxShadow: '0 0 32px var(--accent-glow)',
                  }}
                  whileHover={{ y: -2 }}
                  whileTap={{ y: 0 }}
                >
                  Start Playing <ArrowRight className="h-4 w-4" />
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── SEO CONTENT ── */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">The best free football manager game online in 2026</h2>
          <div className="space-y-4 text-sm md:text-base leading-relaxed" style={{ color: 'var(--text-2)' }}>
            <p>
              My Football Game is a <strong>free football manager game</strong> you can play online without downloading anything.
              Build and manage your own club from the ground up in one of the <strong>top football manager games</strong> available
              in 2026. Unlike other <strong>football management games</strong> that require expensive downloads or subscriptions,
              My Football Game runs entirely in your browser — on desktop, tablet or mobile. Sign up in seconds, get a squad of
              unique players, and start competing against managers from around the world.
            </p>
            <p>
              Whether you're a veteran of football manager simulators or this is your first time managing a virtual
              squad, My Football Game offers the depth you want without the complexity that slows you down. Set your
              formation from 18 options including 4-3-3, 4-4-2, 3-5-2, 4-2-3-1 and more. Assign players to 26
              detailed positions — from sweeper keeper to inside forward. Tweak your mentality, pressing style and
              set-piece routines. Every tactical decision feeds directly into our match simulation engine.
            </p>
            <p>
              The transfer market is where great managers separate themselves. Buy undervalued players from other
              managers, develop them through training, and sell them for profit — or hold onto your stars and build a
              dynasty. Player values fluctuate based on performance, age, and market demand, creating a realistic
              football economy that rewards smart trading.
            </p>
            <p>
              Matches in My Football Game are simulated live, minute by minute. Watch goals, assists, cards, injuries,
              substitutions and momentum shifts unfold in real time. Your tactics, lineup choices and player attributes
              all influence the outcome. After each match, review detailed statistics including shots, possession,
              pass accuracy, player ratings, and man-of-the-match awards.
            </p>
            <p>
              Compete across five divisions, from the Amateur League to the Elite League, with promotion and relegation every season.
              Create private leagues with friends and compete for gold prizes. Climb the global leaderboard for
              coaches, top scorers, assist leaders, and highest-rated players. Challenge friends directly or find
              random opponents for daily competitive matches with bonus coin rewards.
            </p>
            <p>
              My Football Game is completely free to play with no paywall. Earn coins from matches, daily bonuses and
              transfers. Win gold from victories. Everything in the game — every player, every feature, every league —
              is accessible without spending a single penny. Ready to prove you're the best <strong>football manager</strong> online?{' '}
              <Link to="/register" className="font-bold" style={{ color: 'var(--accent)' }}>Create your free club now</Link> and
              start your journey to the top.
            </p>
            <p>
              Looking for alternatives to <strong>Football Manager</strong> (FM) by Sports Interactive? My Football Game offers
              a similar <strong>football manager experience</strong> — squad building, tactical depth, transfer market, player
              development — but completely free and playable in your browser. No 10GB download, no €40 price tag.
              Whether you call it <strong>football manager online</strong>, <strong>soccer manager</strong>, or{' '}
              <strong>football management simulation</strong>, My Football Game delivers. Read our{' '}
              <Link to="/blog/football-manager-online-free-2026" className="underline" style={{ color: 'var(--accent)' }}>
                full comparison with Football Manager
              </Link>{' '}
              or check our{' '}
              <Link to="/blog/top-10-football-games-online-free" className="underline" style={{ color: 'var(--accent)' }}>
                top 10 football games list
              </Link>.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
